import { collectionGroup, getDocs, limit, query } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { platformAttendeeSchema, type PlatformAttendee } from "./schemas";

// One of only two reads in the product that reach PII across the whole
// database (firestore.rules grants `list` on the attendees collection group to
// platform admins, and says so). Capped rather than paged: this is a repair
// surface, not a browsing one, and an unbounded read of every attendee in
// every event is not something to do by accident. The cap being hit is
// reported to the operator rather than hidden.
export const PLATFORM_ATTENDEE_LIMIT = 500;

export type PlatformAttendeesState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; data: PlatformAttendee[]; truncated: boolean };

export function usePlatformAttendees(): PlatformAttendeesState & {
  reload: () => void;
} {
  const [state, setState] = useState<PlatformAttendeesState>({
    status: "loading",
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    // Deliberately unordered. A collection-group orderBy needs its own index,
    // and Firestore drops documents missing the ordered field — which would
    // hide exactly the malformed attendees this screen exists to clean up.
    // Sorted client-side below.
    getDocs(
      query(collectionGroup(db, "attendees"), limit(PLATFORM_ATTENDEE_LIMIT)),
    )
      .then((snap) => {
        if (cancelled) return;
        const attendees: PlatformAttendee[] = [];
        snap.docs.forEach((d) => {
          // events/{eventId}/attendees/{attendeeId} — the grandparent of the
          // document is the event. A collection-group query returns documents
          // from every event, and nothing in the document body carries its
          // event id, so this is the only place it can come from.
          const eventId = d.ref.parent.parent?.id;
          if (!eventId) return;
          const result = platformAttendeeSchema.safeParse({
            eventId,
            attendeeId: d.id,
            ...d.data(),
          });
          if (result.success) attendees.push(result.data);
        });
        attendees.sort(
          (a, b) =>
            a.eventId.localeCompare(b.eventId) ||
            (a.name ?? "").localeCompare(b.name ?? "") ||
            a.attendeeId.localeCompare(b.attendeeId),
        );
        setState({
          status: "success",
          data: attendees,
          truncated: snap.docs.length >= PLATFORM_ATTENDEE_LIMIT,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          error:
            err instanceof Error ? err.message : "Failed to load attendees",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { ...state, reload };
}
